import { InputNumber, Form, Modal, Input, Select, Card, Button, message, Row, Col, DatePicker, Divider, Table, Space, Alert, Segmented } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { reset, selectActiveCart } from "../../redux/cartSlice";
import { useNavigate } from "react-router-dom";
import { generateInvoiceNumber } from "../../utils/generateInvoiceNumber";
import { sendInvoiceToFBR, sendDigitalInvoiceToFBR, sendPRAInvoiceToFBR } from "../../utils/fbrApi";
import getPakistanDateTime from "../../utils/dateTimeUtils";
import { apiRequest } from "../../utils/apiClient";
import checkPosType from "../../utils/posType";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PrintInvoice from "../invoice/PrintInvoice";
import {
  PROVINCES,
  PAYMENT_MODE_CASH,
  PAYMENT_MODE_CREDIT,
  PAYMENT_MODE_CASH_CODE,
  PAYMENT_MODE_CREDIT_CODE,
  PAYMENT_MODE_DEBIT_CARD,
  PAYMENT_MODE_DEBIT_CARD_CODE,
  INVOICE_TYPE_POS,
  INVOICE_TYPE_DIGITAL_INVOICING,
  UOM_NUMBERS_PIECES_UNITS,
} from "../../utils/constants";
import {
  mapCartItemsToFBRDigitalInvoiceItems,
  DEFAULT_SALE_TYPE as MAPPER_DEFAULT_SALE_TYPE,
  FBR_SALE_TYPES,
  getScenarioId,
} from "../../utils/fbrDigitalInvoiceMapper";

const DI_STORAGE_KEYS = {
  fedPayable: "di_fedPayable",
  extraTax: "di_extraTax",
  furtherTax: "di_furtherTax",
};

// Sale types aligned with FBR Digital Invoicing (see fbrDigitalInvoiceMapper)
const SALES_TYPES = FBR_SALE_TYPES;

const DEFAULT_SALES_TYPE = "Goods at standard rate (default)";

/** Returns the customer's sales type if it exists in SALES_TYPES, otherwise the default. */
const getSalesTypeFromCustomer = (customer) => {
  const raw = customer?.salesType;
  if (raw == null || typeof raw !== "string") return DEFAULT_SALES_TYPE;
  const trimmed = raw.trim();
  return trimmed && SALES_TYPES.includes(trimmed) ? trimmed : DEFAULT_SALES_TYPE;
};

const getStoredNumber = (key, defaultVal = 0) => {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return defaultVal;
    const n = Number(v);
    return Number.isFinite(n) ? n : defaultVal;
  } catch {
    return defaultVal;
  }
};

const CreateInvoice = ({ isModalOpen, setIsModalOpen, autoSubmit = false, autoFocusCustomer = false }) => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const cart = useSelector(selectActiveCart);
  const customerNameInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountMode, setDiscountMode] = useState("amount"); // "amount" | "percent"
  // Digital Invoicing: FBR tax fields (persisted in localStorage, default 0)
  const [fedPayable, setFedPayable] = useState(() => getStoredNumber(DI_STORAGE_KEYS.fedPayable));
  const [extraTax, setExtraTax] = useState(() => getStoredNumber(DI_STORAGE_KEYS.extraTax));
  const [furtherTax, setFurtherTax] = useState(() => getStoredNumber(DI_STORAGE_KEYS.furtherTax));
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimateData, setEstimateData] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerListModalOpen, setCustomerListModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const [customerForm] = Form.useForm();
  
  // Payment mode now comes from Basket (CartTotals) and is stored per cart tab
  const paymentMode = cart?.paymentMode || PAYMENT_MODE_CASH;
  const user = useSelector((state) => state.user);

  // console.log({ discount });

  const invoiceType = user?.invoiceType ?? null;
  const businessName = user?.businessName ?? null;
  const checkPos = checkPosType(businessName);

  // Helper function to read FBR toggle state from localStorage (set by CartTotals component)
  const getEnableFBR = () => {
    if (invoiceType === INVOICE_TYPE_POS) {
      const saved = localStorage.getItem("fbrToggleState");
      return saved !== null ? saved === "true" : true; // Default to enabled
    }
    return true; // Always enabled for DIGITAL_INVOICING
  };

  // Helper function to wrap async functions with timeout (only for POS invoices)
  const withTimeout = (promise, timeoutMs, timeoutMessage) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
      ),
    ]);
  };

  // Helper function to show FBR timeout confirmation modal
  const showFBRTimeoutConfirmation = () => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("fbrServerNotResponding"),
        width: 500,
        content: (
          <div>
            <Alert
              message={t("fbrServerNotResponding")}
              description={t("fbrTimeoutDescription")}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </div>
        ),
        okText: t("yesGenerateNormalInvoice"),
        cancelText: tCommon("cancel"),
        okType: "primary",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const res = await apiRequest("/api/customers?page=1&limit=50", {
        method: "GET",
      });

      const items = Array.isArray(res) ? res : res.data || [];
      setCustomers(items);
    } catch (error) {
      console.error("Error fetching customers:", error);
      message.error(t("failedToLoadCustomers"));
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleDeleteCustomer = async (record) => {
    try {
      await apiRequest(`/api/customers/${record._id}`, {
        method: "DELETE",
      });
      message.success(t("customerDeletedSuccess"));
      // Remove locally for snappier UI
      setCustomers((prev) => prev.filter((c) => c._id !== record._id));
    } catch (error) {
      console.error("Customer delete error:", error);
      message.error(error?.message || t("failedToDeleteCustomer"));
    }
  };

  const openCustomerListModal = async () => {
    await fetchCustomers();
    setCustomerListModalOpen(true);
  };

  const handleCreateOrUpdateCustomer = async (values) => {
    try {
      const isEdit = !!selectedCustomer?._id;
      const url = isEdit ? `/api/customers/${selectedCustomer._id}` : "/api/customers";
      const method = isEdit ? "PUT" : "POST";

      const saved = await apiRequest(url, {
        method,
        body: JSON.stringify(values),
      });

      message.success(isEdit ? t("customerUpdatedSuccess") : t("customerCreatedSuccess"));
      setCustomerModalOpen(false);
      customerForm.resetFields();

      // Refresh list so it appears in selector
      await fetchCustomers();

      // Autofill invoice form with this customer
      const customerPayload = saved || selectedCustomer;
      if (customerPayload) {
        const salesType = getSalesTypeFromCustomer(customerPayload);
        form.setFieldsValue({
          customerName: customerPayload.customerName,
          customerPhoneNumber: customerPayload.customerPhoneNumber,
          customerCnicOrNtn: customerPayload.customerCnicOrNtn,
          businessName: customerPayload.businessName,
          salesTaxNumber: customerPayload.salesTaxNumber,
          province: customerPayload.province,
          salesTaxRegistration: customerPayload.salesTaxRegistration,
          salesType,
          customerAddress: customerPayload.customerAddress,
        });
      }
    } catch (error) {
      console.error("Customer save error:", error);
      message.error(error?.message || t("failedToSaveCustomer"));
    }
  };

  const handleSelectCustomer = (record) => {
    setSelectedCustomer(record);
    setCustomerListModalOpen(false);
    // Defer form update to next tick so it runs after modal close; ensures Sales Type and other fields render correctly
    const salesType = getSalesTypeFromCustomer(record);
    const values = {
      customerName: record.customerName,
      customerPhoneNumber: record.customerPhoneNumber,
      customerCnicOrNtn: record.customerCnicOrNtn,
      businessName: record.businessName,
      salesTaxNumber: record.salesTaxNumber,
      province: record.province,
      salesTaxRegistration: record.salesTaxRegistration,
      salesType,
      customerAddress: record.customerAddress,
    };
    setTimeout(() => form.setFieldsValue(values), 0);
  };

  const customerColumns = [
    {
      title: t("customerName"),
      dataIndex: "customerName",
      key: "customerName",
      render: (val) => (val && String(val).trim() ? val : "NA"),
    },
    {
      title: t("phone"),
      dataIndex: "customerPhoneNumber",
      key: "customerPhoneNumber",
      render: (val) => (val && String(val).trim() ? val : "NA"),
    },
    {
      title: t("business"),
      dataIndex: "businessName",
      key: "businessName",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="link"
            onClick={() => handleSelectCustomer(record)}
          >
            {t("useBtn")}
          </Button>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setSelectedCustomer(record);
              setCustomerModalOpen(true);
              const salesType = getSalesTypeFromCustomer(record);
              const values = {
                customerName: record.customerName,
                customerPhoneNumber: record.customerPhoneNumber,
                customerCnicOrNtn: record.customerCnicOrNtn,
                businessName: record.businessName,
                salesTaxNumber: record.salesTaxNumber,
                province: record.province,
                salesTaxRegistration: record.salesTaxRegistration,
                salesType,
                customerAddress: record.customerAddress,
              };
              setTimeout(() => customerForm.setFieldsValue(values), 0);
            }}
          >
            {t("editBtn")}
          </Button>
          <Button
            size="small"
            type="link"
            danger
            onClick={() => {
              Modal.confirm({
                title: t("deleteCustomer"),
                content: t("deleteCustomerConfirmName", { name: record.customerName?.trim() || "NA" }),
                okText: t("delete"),
                okType: "danger",
                cancelText: tCommon("cancel"),
                onOk: () => handleDeleteCustomer(record),
              });
            }}
          >
            {t("delete")}
          </Button>
        </Space>
      ),
    },
  ];

  // Calculate totals safely based on cart items (with per-product discounts)
  const subTotalBeforeDiscount = cart.cartItems.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
    0
  );

  // Calculate total per-product discount (only for POS)
  // item.discount is stored as percentage (e.g., 5 = 5%)
  const totalProductDiscount = invoiceType !== INVOICE_TYPE_DIGITAL_INVOICING
    ? cart.cartItems.reduce((sum, item) => {
      const itemSubtotal = (item.price || 0) * (item.quantity || 0);
      const discountPercent = item.discount || 0;
      const itemDiscountAmount = (itemSubtotal * discountPercent) / 100;
      return sum + itemDiscountAmount;
    }, 0)
    : 0;

  // Subtotal after per-product discounts
  const subTotal = subTotalBeforeDiscount - totalProductDiscount;

  // Calculate tax based on payment mode for POS with checkPos === true
  // If Debit Card is selected, apply 5% tax on subtotal, otherwise use item-level tax
  const calculateTax = () => {
    if (
      invoiceType === INVOICE_TYPE_POS &&
      checkPos === true &&
      paymentMode === PAYMENT_MODE_DEBIT_CARD
    ) {
      // Apply 5% tax on subtotal for Debit Card
      return (subTotal * 5) / 100;
    }
    // Use existing item-level tax calculation
    return cart.cartItems.reduce(
      (sum, item) => sum + (item.tax || 0),
      0
    );
  };

  const totalTax = calculateTax();

  const posFee = 1;

  const safeNumber = (v) => (Number.isFinite(v) ? v : 0);

  const safeSubTotal = safeNumber(subTotal);
  const safeTax = safeNumber(totalTax);
  const safePosFee = safeNumber(posFee);
  const safeProductDiscount = safeNumber(totalProductDiscount);

  // Invoice-level discount: from amount (PKR) or percent of subtotal
  const invoiceDiscountAmount =
    discountMode === "percent"
      ? Math.min(
          (safeSubTotal * Math.min(Math.max(safeNumber(discount), 0), 100)) / 100,
          safeSubTotal
        )
      : Math.min(Math.max(safeNumber(discount), 0), safeSubTotal);

  // Derived percentage for display (amount as % of subtotal)
  const invoiceDiscountPercent =
    safeSubTotal > 0
      ? Math.round((invoiceDiscountAmount / safeSubTotal) * 100 * 100) / 100
      : 0;

  // Total discount = product discounts + invoice-level discount
  const totalDiscountAmount = safeProductDiscount + invoiceDiscountAmount;

  // Total = (subtotal - invoiceDiscountAmount) + tax + posFee
  const totalAmount = Math.max(safeSubTotal - invoiceDiscountAmount + safeTax + safePosFee, 0);

  // Optional: round to 2 decimals
  const finalAmount = Math.round(totalAmount * 100) / 100;
  // console.log({ finalAmount });

  // const totalAmount = subTotal + totalTax + posFee + discount;

  const handleEstimatedInvoice = async () => {
    if (!cart?.cartItems?.length) {
      message.warning(t("cartEmpty"));
      return;
    }

    try {
      // validate current visible fields and grab form values
      const values = await form.validateFields();
      // Ensure paymentMode is always present even though it's no longer a form field
      values.paymentMode = cart?.paymentMode || PAYMENT_MODE_CASH;

      // Recalculate tax for estimate based on payment mode
      const calculateTaxForEstimate = () => {
        if (
          invoiceType === INVOICE_TYPE_POS &&
          checkPos === true &&
          values.paymentMode === PAYMENT_MODE_DEBIT_CARD
        ) {
          // Apply 5% tax on subtotal for Debit Card
          return (safeSubTotal * 5) / 100;
        }
        // Use existing item-level tax calculation
        return cart.cartItems.reduce(
          (sum, item) => sum + (item.tax || 0),
          0
        );
      };
      const estimateTax = calculateTaxForEstimate();
      
      // Recalculate final amount with correct tax
      const estimateTotalAmount = Math.max(
        safeSubTotal - invoiceDiscountAmount + estimateTax + safePosFee,
        0
      );
      const estimateFinalAmount = Math.round(estimateTotalAmount * 100) / 100;

      const estimateInvoiceNumber = `EST-${generateInvoiceNumber()}`;
      const nowISO = new Date().toISOString();

      const printPayload = {
        ...values,
        invoiceNumber: estimateInvoiceNumber,
        createdAt: nowISO,
        invoiceDate: values.invoiceDate ? values.invoiceDate.toDate() : nowISO,
        dueDate: values.dueDate ? values.dueDate.toDate() : undefined,
        cartItems: cart.cartItems,
        subTotal: safeSubTotal.toFixed(2),
        tax: estimateTax.toFixed(2),
        totalAmount: estimateFinalAmount.toFixed(2),
        discount: invoiceDiscountAmount,
        discountPercent: invoiceDiscountPercent,
        productDiscount: safeProductDiscount.toFixed(2), // per-product discount
        fbrInvoiceNumber: undefined, // estimate has no FBR ref
      };

      setEstimateData(printPayload);
      setEstimateOpen(true);
    } catch (err) {
      // antd throws on validation errors; no need to spam console
    }
  };


  // Auto-submit when modal opens with autoSubmit flag (triggered by "0" keyboard shortcut)
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  
  useEffect(() => {
    // Reset auto-submit flag when modal closes
    if (!isModalOpen) {
      setHasAutoSubmitted(false);
      return;
    }
    
    // Auto-submit only for POS invoices when autoSubmit flag is true (from keyboard shortcut)
    // This handles both cases: modal opens with autoSubmit=true, or autoSubmit becomes true after modal opens
    if (isModalOpen && autoSubmit && !loading && !hasAutoSubmitted && invoiceType === INVOICE_TYPE_POS) {
      setHasAutoSubmitted(true); // Mark as submitted to prevent multiple calls
      
      // Use multiple delays to ensure form is fully ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Submit the form - form.submit() will handle validation
            form.submit();
          }, 600); // Delay to ensure form fields are fully rendered and initialized
        });
      });
    }
  }, [isModalOpen, autoSubmit, loading, form, hasAutoSubmitted, invoiceType]);

  // Auto-focus customer name when opened via "9" shortcut (go to basket then create invoice)
  useEffect(() => {
    if (!isModalOpen || !autoFocusCustomer) return;
    const id = setTimeout(() => {
      customerNameInputRef.current?.focus();
    }, 150);
    return () => clearTimeout(id);
  }, [isModalOpen, autoFocusCustomer]);

  // Keyboard shortcut: press "0" when modal is open to submit the form
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (event) => {
      // Only handle "0" key
      if (event.key !== "0") return;

      const activeTag = document.activeElement?.tagName;
      // Don't trigger if the user is typing in an input-like element
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      // Prevent the HomePage handler from firing
      event.preventDefault();
      event.stopPropagation();

      // Submit the form if not already loading
      if (!loading) {
        form.submit();
      }
    };

    // Use capture phase to intercept before HomePage handler
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isModalOpen, loading, form]);

  const onFinish = async (values) => {
    // Prevent multiple simultaneous submissions
    if (loading) {
      return;
    }
    
    setLoading(true);
    const generateInvoice = generateInvoiceNumber();
    // Ensure paymentMode is always present even though it's no longer a form field
    values.paymentMode = cart?.paymentMode || PAYMENT_MODE_CASH;

    const userId = user?.id || "";

    // Build actual FBR invoice payload from cart + form values
    const { date, time } = getPakistanDateTime(new Date().toISOString());

    const totalQuantity = cart.cartItems.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );

    const round2 = (num) => Number(Number(num).toFixed(2));

    let fbrItems = [];

    if (invoiceType === "DIGITAL_INVOICING") {
      // FBR Digital Invoicing: law-aligned, PRAL-safe, Annex-C compliant mapper
      const saleType = values.salesType || MAPPER_DEFAULT_SALE_TYPE;
      const buyerRegistered = values.salesTaxRegistration === "Registered";

      fbrItems = mapCartItemsToFBRDigitalInvoiceItems(cart.cartItems, {
        invoiceDiscountAmount: round2(invoiceDiscountAmount),
        subTotal: safeSubTotal > 0 ? safeSubTotal : 1,
        saleType,
        buyerRegistered,
        isWithholdingApplicable: false, // set from customer/context when buyer is withholding agent
        withholdingRate: 0,
        fedPayable: round2(Number(fedPayable) || 0),
        extraTax: round2(Number(extraTax) || 0),
        furtherTax: round2(Number(furtherTax) || 0),
        defaultUom: UOM_NUMBERS_PIECES_UNITS,
      });
    } else {
      // FBR POS RETAINER (INVOICE API)
      // Check if we need to apply 5% tax for Debit Card
      const shouldApplyDebitCardTax = 
        invoiceType === INVOICE_TYPE_POS &&
        checkPos === true &&
        values.paymentMode === PAYMENT_MODE_DEBIT_CARD;
      
      // Calculate total tax for Debit Card (5% of subtotal) if applicable
      const debitCardTotalTax = shouldApplyDebitCardTax
        ? round2((safeSubTotal * 5) / 100)
        : null;
      
      fbrItems = cart.cartItems.map((item) => {
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.price) || 0;
        const saleValue = quantity * unitPrice;
        const taxRate = Number(item.taxRate) || 0;
        
        // Calculate tax: if Debit Card, distribute 5% tax proportionally, otherwise use item tax
        let taxCharged;
        if (shouldApplyDebitCardTax && debitCardTotalTax !== null) {
          // Distribute 5% tax proportionally based on item's sale value
          const itemProportion = subTotalBeforeDiscount > 0 
            ? saleValue / subTotalBeforeDiscount 
            : 0;
          taxCharged = round2(debitCardTotalTax * itemProportion);
        } else {
          taxCharged = Number(item.tax) || 0;
        }
        
        // item.discount is percentage, calculate actual discount amount
        const discountPercent = item.discount || 0;
        const itemDiscountAmount = round2((saleValue * discountPercent) / 100);

        return {
          ItemCode: item._id || item.id || "",
          ItemName: item.title || t("product"),
          Quantity: quantity,
          PCTCode: item.hsCode || item.hscode || "",
          TaxRate: shouldApplyDebitCardTax ? 5 : taxRate, // Use 5% rate for Debit Card
          SaleValue: saleValue,
          TaxCharged: taxCharged,
          Discount: itemDiscountAmount, // Send actual discount amount to FBR
          TotalAmount: saleValue + taxCharged - itemDiscountAmount,
          InvoiceType: 1, // normal POS sale
        };
      });
    }

    // Calculate total discount for FBR (per-product discounts only for POS)
    const totalDiscountForFBR = invoiceType !== INVOICE_TYPE_DIGITAL_INVOICING
      ? round2(totalProductDiscount)
      : 0;

    // Recalculate tax for FBR invoice based on payment mode
    // (ensures we use the correct tax even if component hasn't re-rendered)
    const calculateTaxForInvoice = () => {
      if (
        invoiceType === INVOICE_TYPE_POS &&
        checkPos === true &&
        values.paymentMode === PAYMENT_MODE_DEBIT_CARD
      ) {
        // Apply 5% tax on subtotal for Debit Card
        return round2((safeSubTotal * 5) / 100);
      }
      // Use existing item-level tax calculation
      return round2(cart.cartItems.reduce(
        (sum, item) => sum + (item.tax || 0),
        0
      ));
    };
    const invoiceTotalTax = calculateTaxForInvoice();
    
    // Recalculate final amount with correct tax for FBR invoice
    const invoiceTotalAmount = Math.max(
      safeSubTotal - invoiceDiscountAmount + invoiceTotalTax + safePosFee,
      0
    );
    const invoiceFinalAmount = Math.round(invoiceTotalAmount * 100) / 100;

    const paymentModeCode =
      values.paymentMode === PAYMENT_MODE_CASH
        ? PAYMENT_MODE_CASH_CODE
        : values.paymentMode === PAYMENT_MODE_DEBIT_CARD
          ? PAYMENT_MODE_DEBIT_CARD_CODE
          : PAYMENT_MODE_CREDIT_CODE;

    const invoice = {
      InvoiceNumber: generateInvoice,
      USIN: "USIN0", // will be returned by FBR in the response
      DateTime: `${date} ${time}`,
      TotalBillAmount: invoiceFinalAmount,
      TotalQuantity: totalQuantity,
      TotalSaleValue: round2(subTotalBeforeDiscount),
      TotalTaxCharged: invoiceTotalTax,
      PaymentMode: paymentModeCode,
      Discount: totalDiscountForFBR,
      InvoiceType: 1,
      Items: fbrItems,
    };

    const digitalInvoice = {
      invoiceType: "Sale Invoice",
      invoiceDate: values.invoiceDate?.format("YYYY-MM-DD"),

      sellerBusinessName: user?.businessName || "",
      sellerNTNCNIC: user?.cnic || "",
      sellerProvince: user?.province || "",
      sellerAddress: user?.address || "",

      buyerNTNCNIC: values.customerCnicOrNtn,
      buyerBusinessName: values.customerName?.trim() || values.businessName,
      buyerProvince: values.province,
      buyerAddress: values.customerAddress,
      buyerRegistrationType: values.salesTaxRegistration,

      invoiceRefNo: generateInvoice,
      // SN001–SN028: scenario depends on sale type and buyer registration
      scenarioId: getScenarioId(values.salesType, values.salesTaxRegistration === "Registered"),

      items: fbrItems,
      discount: round2(invoiceDiscountAmount),
    };

    const enableFBR = getEnableFBR();
    const shouldSendToFBR = invoiceType === "DIGITAL_INVOICING" ||
                            (invoiceType === "POS" && enableFBR);

    try {
      let fbrInvoiceNumber = null;
      let backendFbrInvoiceNumber = null;

      // 1️⃣ Send invoice to FBR based on type (only if enabled)
      if (shouldSendToFBR) {
        try {
          // For POS invoices, apply 10-second timeout
          const isPOSInvoice = invoiceType === "POS";
          const FBR_TIMEOUT_MS = 10000; // 10 seconds
          
          if (invoiceType === "DIGITAL_INVOICING") {
            fbrInvoiceNumber = await sendDigitalInvoiceToFBR({
              userId,
              invoice: digitalInvoice,
            });
          } else if (isPOSInvoice) {
            // Apply timeout wrapper for POS invoices only
            try {
              const fbrPromise = checkPos === true
                ? sendPRAInvoiceToFBR({ userId, invoice })
                : sendInvoiceToFBR({ userId, invoice });
              
              fbrInvoiceNumber = await withTimeout(
                fbrPromise,
                FBR_TIMEOUT_MS,
                "FBR_TIMEOUT"
              );
            } catch (timeoutErr) {
              // Handle timeout specifically for POS invoices
              if (timeoutErr.message === "FBR_TIMEOUT") {
                console.warn("FBR request timed out after 10 seconds");
                
                // Show confirmation modal and wait for user response
                const userConfirmed = await showFBRTimeoutConfirmation();
                
                if (!userConfirmed) {
                  // User cancelled - stop invoice creation
                  setLoading(false);
                  return;
                }
                
                // User confirmed - continue with normal invoice
                message.info(t("generatingNormalInvoice"), 3);
                fbrInvoiceNumber = null;
                backendFbrInvoiceNumber = null;
                // Skip validation and continue with normal invoice creation
              } else {
                // Re-throw other errors to be handled below
                throw timeoutErr;
              }
            }
          }

          // Validate FBR response only if we got a valid response (not timed out)
          if (fbrInvoiceNumber !== null && fbrInvoiceNumber !== undefined) {
            if (!fbrInvoiceNumber) {
              throw new Error("FBR did not return invoice number");
            }
          }

        } catch (err) {
          // Only show error and stop execution if it's not a timeout (timeout already handled above)
          // or if it's a DIGITAL_INVOICING invoice (which should always require FBR)
          if (invoiceType === "DIGITAL_INVOICING" || err.message !== "FBR_TIMEOUT") {
            console.error("Failed to send invoice to FBR:", err);
            message.error(`${t("failedToCommunicateWithFBR")} ${err?.message || ""}`.trim(), 5); // 5 second duration
            setLoading(false); // Reset loading state before returning
            return; // Stop execution
          }
          // For POS timeout, we already handled it above, so continue
        }

        // 2️⃣ Extract FBR invoice number (only if we got a response)
        if (fbrInvoiceNumber) {
          backendFbrInvoiceNumber =
            fbrInvoiceNumber?.fbrResponse?.invoiceNumber ||
            fbrInvoiceNumber?.fbrResponse?.InvoiceNumber ||
            "0";

          // 3️⃣ Validate FBR response based on invoice type
          let isFbrValid = false;
          let errorMsg = t("fbrRejectedInvoice");

          if (invoiceType === "DIGITAL_INVOICING") {
            // Digital Invoicing validation
            const validation = fbrInvoiceNumber?.fbrResponse?.validationResponse;
            isFbrValid =
              validation?.statusCode === "00" &&
              validation?.invoiceStatuses?.every((item) => item.statusCode === "00");

            if (!isFbrValid) {
              errorMsg =
                validation?.invoiceStatuses?.[0]?.error ||
                validation?.error ||
                errorMsg;
            }
          } else {
            // POS side validation - check Code === "100" for success
            const fbrCode = fbrInvoiceNumber?.fbrResponse?.Code;
            isFbrValid = fbrCode === "100";

            if (!isFbrValid) {
              errorMsg =
                fbrInvoiceNumber?.fbrResponse?.Response ||
                fbrInvoiceNumber?.fbrResponse?.error ||
                errorMsg;
            }
          }

          if (!isFbrValid) {
            message.error(errorMsg, 5); // 5 second duration
            setLoading(false); // Reset loading state before returning
            return; // Stop execution, do not create invoice
          }
        }
        // If fbrInvoiceNumber is null (timeout case), skip validation and continue with normal invoice
      } else {
        // FBR is disabled for POS invoice - set to null
        backendFbrInvoiceNumber = null;
      }

      // 4️⃣ Only now add invoice in backend (includes stock validation and update)
      let createdInvoice;
      try {
        // Total discount **amount** (product-level + invoice-level), rounded to 2 decimals
        const discountAmountForDb = Number(totalDiscountAmount.toFixed(2));
        
        // Recalculate final amount with correct tax for backend
        const backendTotalAmount = Math.max(
          safeSubTotal - invoiceDiscountAmount + invoiceTotalTax + safePosFee,
          0
        );
        const backendFinalAmount = Math.round(backendTotalAmount * 100) / 100;

        const payload = {
          ...values,
          invoiceType,
          subTotal: Number(safeSubTotal.toFixed(2)),
          tax: Number(invoiceTotalTax.toFixed(2)),
          totalAmount: Number(backendFinalAmount.toFixed(2)),
          cartItems: cart.cartItems,
          invoiceNumber: generateInvoice,
          fbrInvoiceNumber: backendFbrInvoiceNumber !== null ? (backendFbrInvoiceNumber || "0") : null,
          userId: userId || "0",
          discount: discountAmountForDb > 0 ? discountAmountForDb : null,
        };
        const effectiveBranchId = user?.branchId || user?.selectedBranchId;
        if (effectiveBranchId) payload.branchId = effectiveBranchId;
        if (invoiceType === INVOICE_TYPE_DIGITAL_INVOICING) {
          payload.fedPayable = round2(Number(fedPayable) || 0);
          payload.extraTax = round2(Number(extraTax) || 0);
          payload.furtherTax = round2(Number(furtherTax) || 0);
          payload.discount = Number(discountAmountForDb.toFixed(2));
          payload.discountPercent =
            safeSubTotal > 0 && invoiceDiscountAmount > 0
              ? Math.round((invoiceDiscountAmount / safeSubTotal) * 100 * 100) / 100
              : null;
        }
        createdInvoice = await apiRequest("/api/invoices/add-invoice", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (invoiceError) {
        // Handle stock validation errors from backend
        if (
          invoiceError.message &&
          invoiceError.message.includes("Stock validation failed")
        ) {
          // Extract error details from the error data
          const errorDetails =
            invoiceError.data?.errors && Array.isArray(invoiceError.data.errors)
              ? invoiceError.data.errors
              : [invoiceError.message];

          message.error({
            content: (
              <div>
                <div style={{ marginBottom: 8, fontWeight: "bold" }}>
                  {t("stockValidationFailed")}
                </div>
                {errorDetails.map((error, idx) => (
                  <div key={idx} style={{ marginBottom: 4 }}>
                    • {error}
                  </div>
                ))}
              </div>
            ),
            duration: 8,
          });
        } else {
          throw invoiceError; // Re-throw other errors
        }
        setLoading(false); // Reset loading state before returning
        return; // Stop execution if invoice creation failed
      }

      // 5️⃣ Show success only after everything passes
      message.success(t("invoiceGeneratedSuccess"));
      setLoading(false);
      setIsModalOpen(false);
      dispatch(reset());

      // Navigate to invoices page and pass the freshly created invoice for reliable auto-print
      navigate("/invoices", {
        state: {
          autoPrint: true,
          invoice: createdInvoice || null,
        },
      });
    } catch (error) {
      // Preserve server-provided detailed messages if available
      if (error.message) {
        message.error(error.message);
      } else {
        message.error(t("operationFailed"));
      }
      console.error(error);
    } finally {
      setLoading(false)
    }
  };

  return (
    <>
      <Modal
        width={1100}
        title={t("createInvoice")}
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            invoiceDate: dayjs(), // current date
          }}
        >
          {invoiceType === INVOICE_TYPE_DIGITAL_INVOICING && (
            <Row gutter={[16, 8]} className="mb-4">
              <Col span={24}>
                <Button
                  type="dashed"
                  onClick={() => {
                    setSelectedCustomer(null);
                    customerForm.resetFields();
                    setCustomerModalOpen(true);
                  }}
                >
                  {t("createCustomer")}
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={openCustomerListModal}
                >
                  {t("selectCustomer")}
                </Button>
              </Col>
            </Row>
          )}

          {/* ================= BASIC INFO ================= */}
          <Divider orientation="left">{t("invoiceDetails")}</Divider>

          <Row gutter={[16, 8]}>

            <Col span={6}>
              <Form.Item
                name="invoiceDate"
                label={t("invoiceDate")}
                rules={[{ required: true, message: t("selectInvoiceDate") }]}
              >
                <DatePicker
                  className="w-full"
                  format="YYYY-MM-DD"
                  placeholder={t("selectDate")}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="dueDate" label={t("dueDate")}>
                <DatePicker
                  className="w-full"
                  format="YYYY-MM-DD"
                  placeholder={t("selectDate")}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="customerName"
                label={t("customerName")}
                rules={[{ message: t("customerNameRequired") }]}
              >
                <Input ref={customerNameInputRef} placeholder={t("customerNamePlaceholder")} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="customerPhoneNumber"
                label={t("customerPhoneNumber")}
                rules={[
                  { message: t("phoneNumberRequired") },
                  {
                    pattern: /^(0\d{9,10})$/, // Pakistan: starts with 0, 10-11 digits
                    message: t("validPakistanPhone"),
                  },
                ]}
              >
                <Input
                  placeholder={t("customerPhonePlaceholder")}
                  maxLength={11} // Max length for standard Pakistani numbers
                  onKeyPress={(e) => {
                    // Allow only digits
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ================= DIGITAL INVOICING ================= */}
          {invoiceType === INVOICE_TYPE_DIGITAL_INVOICING && (
            <>
              <Divider orientation="left">{t("customerTaxDetails")}</Divider>

              <Row gutter={[16, 8]}>
                <Col span={6}>
                  <Form.Item
                    name="customerCnicOrNtn"
                    label={t("cnicNtn")}
                    rules={[{ required: true, message: t("cnicNtnRequired") }]}
                  >
                    <Input
                      placeholder={t("cnicPlaceholder")}
                      maxLength={13}
                      onKeyPress={(e) => {
                        // Allow only digits
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="businessName"
                    label={t("businessName")}
                    rules={[{ required: true, message: t("businessNameRequired") }]}
                  >
                    <Input placeholder={t("businessNamePlaceholder")} />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="salesTaxNumber"
                    label={t("salesTaxNo")}
                    rules={[{ required: true, message: t("salesTaxNumberRequired") }]}
                  >
                    <Input placeholder={t("salesTaxNumber")} inputMode="numeric" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="province"
                    label={t("province")}
                    rules={[{ required: true, message: t("selectProvinceRequired") }]}
                  >
                    <Select placeholder={t("selectProvince")} options={PROVINCES} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 8]}>
                <Col span={6}>
                  <Form.Item
                    name="salesTaxRegistration"
                    label={t("salesTaxRegistration")}
                    rules={[{ required: true, message: t("selectRegistrationType") }]}
                  >
                    <Select placeholder={t("selectType")}>
                      <Select.Option value="Registered">
                        {t("registered")}
                      </Select.Option>
                      <Select.Option value="Unregistered">
                        {t("unregistered")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="challanNumber"
                    label={t("challanNumber")}
                  >
                    <Input type="number" placeholder={t("enterChallanNo")} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="customerAddress"
                    label={t("businessAddress")}
                    rules={[{ required: true, message: t("addressRequired") }]}
                  >
                    <TextArea rows={2} placeholder={t("completeAddress")} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <Form.Item name="salesType" label={t("salesType")}>
                    <Select
                      placeholder={t("selectSalesType")}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={SALES_TYPES.map((label) => ({ label, value: label }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">{t("fbrTaxFields")}</Divider>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <Form.Item label={t("fedPayable")}>
                    <InputNumber
                      className="w-full"
                      min={0}
                      precision={2}
                      value={fedPayable}
                      onChange={(v) => {
                        const val = v ?? 0;
                        setFedPayable(val);
                        localStorage.setItem(DI_STORAGE_KEYS.fedPayable, String(val));
                      }}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t("extraTax")}>
                    <InputNumber
                      className="w-full"
                      min={0}
                      precision={2}
                      value={extraTax}
                      onChange={(v) => {
                        const val = v ?? 0;
                        setExtraTax(val);
                        localStorage.setItem(DI_STORAGE_KEYS.extraTax, String(val));
                      }}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t("furtherTax")}>
                    <InputNumber
                      className="w-full"
                      min={0}
                      precision={2}
                      value={furtherTax}
                      onChange={(v) => {
                        const val = v ?? 0;
                        setFurtherTax(val);
                        localStorage.setItem(DI_STORAGE_KEYS.furtherTax, String(val));
                      }}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* ================= PAYMENT ================= */}
          <Divider orientation="left">{t("payment")}</Divider>

          <Row gutter={[16, 8]}>
            <Col span={6}>
              <Form.Item
                label={t("discount")}
                style={{ marginBottom: 0 }}
              >
                <div className="flex gap-2 items-center w-full">
                  <Segmented
                    value={discountMode}
                    onChange={setDiscountMode}
                    options={[
                      { label: t("discountModeAmount"), value: "amount" },
                      { label: t("discountModePercent"), value: "percent" },
                    ]}
                    size="small"
                  />
                  <Form.Item
                    name="discount"
                    noStyle
                    rules={[
                      { type: "number", min: 0, message: t("discountMinZero") },
                      ...(discountMode === "percent"
                        ? [{ type: "number", max: 100, message: t("discountMaxPercent") }]
                        : []),
                    ]}
                  >
                    <InputNumber
                      className="flex-1 min-w-0"
                      min={0}
                      max={discountMode === "percent" ? 100 : undefined}
                      precision={2}
                      value={discount}
                      onChange={(value) => setDiscount(value ?? 0)}
                      placeholder={
                        discountMode === "percent"
                          ? t("discountPlaceholderPercent")
                          : t("discountPlaceholder")
                      }
                      addonAfter={discountMode === "percent" ? "%" : undefined}
                      size="small"
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {/* ================= SUMMARY ================= */}
          <Card className="mt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">{t("subtotalBeforeDiscount")}</span>
              <span className="text-gray-800">{subTotalBeforeDiscount.toFixed(2)}</span>
            </div>

            {invoiceType !== INVOICE_TYPE_DIGITAL_INVOICING && safeProductDiscount > 0 && (
              <div className="flex justify-between my-2 text-sm">
                <span className="text-gray-700">{t("productDiscount")}</span>
                <span className="text-green-600">-{safeProductDiscount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between my-2 text-sm">
              <span className="text-gray-700">{t("subtotal")}</span>
              <span className="text-gray-800">{safeSubTotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-between my-2 text-sm">
              <span className="text-gray-700">{t("govtTaxes")}</span>
              <span className="text-red-600">+{safeTax.toFixed(2)}</span>
            </div>

            {invoiceDiscountAmount > 0 && (
              <div className="flex justify-between my-2 text-sm">
                <span className="text-gray-700">
                  {t("invoiceDiscount")} ({invoiceDiscountPercent}%)
                </span>
                <span className="text-green-600">-{invoiceDiscountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between my-2 text-sm">
              <span className="text-gray-700">{t("posFee")}</span>
              <span className="text-gray-800">{safePosFee.toFixed(2)}</span>
            </div>

            <Divider />

            <div className="flex justify-between text-base">
              <b className="font-semibold text-gray-800">{t("total")}</b>
              <b className="font-semibold text-gray-800">{finalAmount.toFixed(2)}</b>
            </div>

            <div className="flex justify-end mt-4">
              {checkPos === false && <Button
                className="mr-4"
                type="default"
                htmlType="button"
                size="large"
                onClick={handleEstimatedInvoice}
                disabled={loading}
              >
                {t("estimatedInvoice")}
              </Button>}

              <Button type="primary" htmlType="submit" size="large" loading={loading}>
                {t("createInvoice")}
              </Button>
            </div>
          </Card>
        </Form>
      </Modal>

      {/* Create / Edit Customer */}
      <Modal
        title={selectedCustomer ? t("editCustomer") : t("createCustomer")}
        open={customerModalOpen}
        onCancel={() => {
          setCustomerModalOpen(false);
          customerForm.resetFields();
          setSelectedCustomer(null);
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={customerForm}
          layout="vertical"
          onFinish={handleCreateOrUpdateCustomer}
        >
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Form.Item
                name="customerName"
                label={t("customerName")}
              >
                <Input placeholder={t("customerNamePlaceholder")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customerPhoneNumber"
                label={t("customerPhoneNumber")}
                rules={[
                  {
                    pattern: /^(0\d{9,10})$|^$/, // Pakistan: starts with 0, 10-11 digits, or empty
                    message: t("validPakistanPhone"),
                  },
                ]}
              >
                <Input placeholder={t("customerPhonePlaceholder")} maxLength={11} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Form.Item
                name="customerCnicOrNtn"
                label={t("cnicNtn")}
                rules={[{ required: true, message: t("cnicNtnRequired") }]}
              >
                <Input placeholder={t("cnicPlaceholder")} maxLength={13} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="businessName"
                label={t("businessName")}
                rules={[{ required: true, message: t("businessNameRequired") }]}
              >
                <Input placeholder={t("businessNamePlaceholder")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Form.Item
                name="salesTaxNumber"
                label={t("salesTaxNo")}
                rules={[
                  { required: true, message: t("salesTaxNumberRequired") },
                  {
                    pattern: /^\d+$/,
                    message: t("salesTaxDigitsOnly"),
                  },
                ]}
              >
                <Input
                  placeholder={t("salesTaxNumber")}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="province"
                label={t("province")}
                rules={[{ required: true, message: t("selectProvinceRequired") }]}
              >
                <Select placeholder={t("selectProvince")} options={PROVINCES} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Form.Item
                name="salesTaxRegistration"
                label={t("salesTaxRegistration")}
                rules={[{ required: true, message: t("selectRegistrationType") }]}
              >
                <Select placeholder={t("selectType")}>
                  <Select.Option value="Registered">{t("registered")}</Select.Option>
                  <Select.Option value="Unregistered">{t("unregistered")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customerAddress"
                label={t("customerAddress")}
                rules={[{ required: true, message: t("addressRequired") }]}
              >
                <Input.TextArea rows={2} placeholder={t("completeAddress")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item name="salesType" label={t("salesType")}>
                <Select
                  placeholder={t("selectSalesType")}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={SALES_TYPES.map((label) => ({ label, value: label }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="flex justify-end mt-4">
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                setCustomerModalOpen(false);
                customerForm.resetFields();
                setSelectedCustomer(null);
              }}
            >
              {tCommon("cancel")}
            </Button>
            <Button type="primary" htmlType="submit">
              {selectedCustomer ? t("updateCustomer") : t("createCustomer")}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Select Customer */}
      <Modal
        title={t("selectCustomer")}
        open={customerListModalOpen}
        onCancel={() => setCustomerListModalOpen(false)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <Table
          rowKey="_id"
          dataSource={customers}
          columns={customerColumns}
          loading={customersLoading}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* Client-side estimate preview/print only (no server, no FBR) */}
      <PrintInvoice
        isModalOpen={estimateOpen}
        setIsModalOpen={setEstimateOpen}
        printData={estimateData}
        isEstimate
        autoPrint
      />
    </>

  );
};

export default CreateInvoice;
